<template>
  <div>
    <cc-collapse :title="$t('ct-settings')">
      <div class="form-group">
        <label for="lottie-file-checkbox-url">{{ $t('ct-external-url') }}</label>
        <input
          v-model="config.url"
          class="form-control"
          for="lottie-file-checkbox-url"
          type="text"
          @change="setInputOption('url', config.url)"
        >
      </div>

      <div class="form-group">
        <p>{{ $t('ct-or') }}</p>
        <div class="d-flex justify-content-center">
          <button
            v-if="config.code"
            class="btn btn-sm btn-secondary btn-small btn-new-page"
            type="button"
            @click.prevent="openUploadLottieFile('upload-lottie-file')">{{ $t('ct-upload-lottie-json') }}</button>
          <button
            v-else
            class="btn btn-sm btn-secondary btn-small btn-new-page"
            type="button"
            @click.prevent="openUploadLottieFile('see-lottie-files')">{{ $t('ct-upload-lottie-file') }}</button>
          <button
            v-tooltip="{content:$t('ct-delete'), class: 'tooltip-custom'}"
            v-if="config.code"
            class="btn btn-danger float-right px-3 mx-1"
            @click.prevent="removeLottieFile()">
            <font-awesome-icon :icon="['far', 'trash-alt']"/>
          </button>
        </div>
      </div>

      <div class="form-group">
        <div class="form-check">
          <input
            id="lottie-file-checkbox-autoplay"
            v-model="config.autoplay"
            :disabled="!config.controls"
            class="form-check-input"
            type="checkbox"
            @change="setInputOption('autoplay', config.autoplay)">
          <label class="form-check-label" for="lottie-file-checkbox-autoplay">{{ $t('ct-autoplay') }}</label>
        </div>

        <div class="form-check">
          <input
            id="lottie-file-checkbox-loop"
            v-model="config.loop"
            class="form-check-input"
            type="checkbox"
            @change="setInputOption('loop', config.loop)">
          <label class="form-check-label" for="lottie-file-checkbox-loop">{{ $t('ct-loop') }}</label>
        </div>

        <div class="form-check">
          <input
            id="lottie-file-checkbox-controls"
            v-model="config.controls"
            class="form-check-input"
            type="checkbox"
            @change="setInputOption('controls', config.controls)">
          <label class="form-check-label" for="lottie-file-checkbox-controls">{{ $t('ct-controls') }}</label>
        </div>
      </div>

      <div class="form-group">
        <label for="lottie-file-checkbox-speed">{{ $t('ct-speed') }}</label>
        <input
          v-model.number="config.speed"
          class="form-control"
          for="lottie-file-checkbox-speed"
          min="1"
          max="2"
          type="number"
          @change="setInputOption('speed', config.speed)">
      </div>
    </cc-collapse>

    <cc-collapse :title="$t('ct-sizes')">
      <cc-slider-component ref="widthConfig"/>
      <cc-slider-component ref="heightConfig"/>
      <cc-slider-component ref="minHeightConfig"/>
      <cc-slider-component ref="minWidthTabletConfig"/>
    </cc-collapse>

    <cc-collapse :title="$t('ct-background-settings')">
      <cc-box-color ref="backgroundColor"/>
    </cc-collapse>

    <cc-collapse :title="$t('ct-tag')">
      <cc-tag ref="tag"/>
    </cc-collapse>

    <cc-collapse :title="$t('ct-padding')" class="padding-mobile">
      <cc-padding ref="padding"/>
    </cc-collapse>

    <cc-collapse :title="$t('ct-layers')">
      <cc-z-index ref="z-index"/>
    </cc-collapse>

    <sweet-modal
      ref="lottieFileSettings"
      :title="$t('ct-upload-lottie-json')"
      blocking>
      <sweet-modal-tab id="see-lottie-files" ref="tab-see-lottie-files" :title="$t(`ct-tab-see-lottie-files`)">
        <div v-if="userLottieFiles.length > 0" class="user-lottie-files-content">
          <div class="user-lottie-files__list">
            <div
              v-for="(file, index) in userLottieFiles"
              :class="{'lottie-files-selected' : selectFile === file.code}"
              class="user-lottie-files__list-item"
            >
              <lottie-vue-player
                :key="`lottie-player-${index}`"
                :src="`${getUrl()}/api/get-lottie-files/${file.code}.json`"
                :loop="true"
                :autoplay="true"
                style="height: 200px"/>

              <div class="user-lottie-files__list-item-info" @click="selectLottieFile(file)">
                <span>{{ file.name }}</span>
                <button
                  v-tooltip="{content:$t('ct-delete'), class: 'tooltip-custom'}"
                  class="btn btn-danger float-right px-3 mx-1"
                  @click.prevent="deleteLottieFile(file, index)">
                  <font-awesome-icon :icon="['far', 'trash-alt']"/>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="user-lottie-files-content">
          {{ $t('ct-upload-lottie-list-is-empty') }}
        </div>

        <div class="d-flex justify-content-end">
          <button
            :disabled="!selectFile"
            :class="{'btn-secondary' : !selectFile}"
            class="btn btn-success mt-2"
            @click.prevent="saveLottieFile"
          >{{ $t('ct-upload-lottie-save') }}</button>
        </div>
      </sweet-modal-tab>

      <sweet-modal-tab id="upload-lottie-file"  ref="tab-upload-lottie-file" :title="$t(`ct-tab-upload-lottie-file`)">
        <div class="form-group">
          <p>{{ $t('ct-upload-lottie-json-obs') }}</p>

          <label for="lottie-file-upload-json-file">{{ $t('ct-upload') }}</label>
          <input
            id="lottie-file-upload-json-file"
            ref="inputUploadLottieFile"
            type="file"
            accept=".json"
            @change="changeUpload">
        </div>

        <div id="preview-content-lottie-file" style="width: 360px; max-height: 70vh; margin: 0 auto">
          <p><strong>{{ $t('ct-preview') }}:</strong></p>
          <p>{{ $t('ct-upload-lottie-json-preview-obs') }}</p>
          <div id="preview-lottie-file"/>
        </div>
        <div class="d-flex justify-content-end">
          <button
            :disabled="!file.isValid"
            class="btn btn-primary mt-2"
            @click.prevent="uploadLottieFile"
          >{{ $t('ct-upload-lottie-json-is-valid') }}</button>
        </div>
      </sweet-modal-tab>
    </sweet-modal>
  </div>
</template>

<script>

import AlertBoxComponent from '../../../commons/AlertBoxComponent';
import ToggleButtonComponent from '../../../commons/ToggleButtonComponent';
import {SweetModal, SweetModalTab} from "sweet-modal-vue";
import Lottie from 'lottie-web';
import {createLottieFile, excludeLottieFile, getLottieFiles} from "../../../../js/services/lottieFiles";
import {getUrl} from "../../../../js/services/http";

export default {
  name: 'Options',
  components: {
    AlertBoxComponent,
    ToggleButtonComponent,
    SweetModal,
    SweetModalTab
  },
  data() {
    return {
      config: {
        url: '',
        code: '',
        speed: 1,
        autoplay: false,
        loop: false,
        controls: false
      },
      file: {
        isValid: false,
        upload: null,
        name: ''
      },
      selectFile: '',
      userLottieFiles: [],
      callback: () => {
      }
    };
  },
  watch: {
    'config.url' : function() {
      this.setInputOption('url', this.config.url);
    }
  },
  beforeDestroy() {
    this.config = {};
    this.callback = () => {
    };
  },
  mounted(){
    this.reloadUserLottieFiles();
  },
  methods: {
    getUrl,
    removeLottieFile() {
      this.config.code = '';
      this.save();
    },
    reloadUserLottieFiles() {
      this.file = {
        isValid: false,
        upload: null,
        name: ''
      };

      getLottieFiles()
        .then(({data}) => {
          this.userLottieFiles = data;
        });
    },
    selectLottieFile(file) {
      this.selectFile = file.code;
    },
    saveLottieFile() {
      if(this.selectFile){
        this.config.code = this.selectFile;
        this.save();
        this.closeUploadLottieFile();
      }
    },
    init(config, callback) {
      let vm = this;


      vm.config = _.clone(config);

      vm.callback = callback;

      // sizes
      this.$refs['widthConfig'].setValues({
        defaultValue: vm.config.width,
        defaultValueChecked: true,
        min: 50,
        max: 1400,
        canBeCheckbox: false,
        labelDesc: 'width',
        name: 'iconColor',
        callback: vm.setWidth
      });

      this.$refs['heightConfig'].setValues({
        defaultValue: vm.config.height,
        defaultValueChecked: true,
        min: 10,
        max: 800,
        canBeCheckbox: false,
        labelDesc: 'height',
        name: 'iconColor',
        callback: vm.setHeight
      });

      this.$refs['minHeightConfig'].setValues({
        defaultValue: vm.config.minHeight,
        defaultValueChecked: true,
        min: 10,
        max: 800,
        canBeCheckbox: false,
        labelDesc: 'min-height',
        name: 'iconColor',
        callback: vm.setMinHeight
      });

      this.$refs['minWidthTabletConfig']
        .setValues({
          defaultValue: vm.config.maxWidth,
          defaultValueChecked: true,
          min: 50,
          max: 800,
          canBeCheckbox: false,
          labelDesc: 'max-width-tablet',
          name: 'iconColor',
          callback: vm.setMaxWidth
        });

      // padding
      this.$refs['padding'].setValues({
        defaultTop: vm.config.paddingTop,
        defaultBottom: vm.config.paddingBottom,
        defaultLeft: vm.config.paddingLeft,
        defaultRight: vm.config.paddingRight,
        min: 0,
        max: 150,
        labelDesc: 'padding',
        callback: vm.setPadding
      });

      // tag
      this.$refs['tag'].setValues({tag: vm.config.tag, callback: vm.setTag});

      // z-index
      this.$refs['z-index'].setValues({defaultValue: vm.config.zIndex, callback: vm.setZIndex});

      this.$refs['backgroundColor'].setValues({defaultValue: vm.config.backgroundColor, gradient: true, labelDesc: 'bg-color', name: 'backgroundColor', callback: vm.setBackgroundColor});
    },
    setPadding(data) {
      this.config.paddingTop = `${data.paddingTop}px`;
      this.config.paddingBottom = `${data.paddingBottom}px`;
      this.config.paddingLeft = `${data.paddingLeft}px`;
      this.config.paddingRight = `${data.paddingRight}px`;
      this.save();
    },
    setInputOption(type, value) {
      if (type === 'controls' && value && this.config.autoplay)
        this.config.autoplay = true;

      this.config[type] = value;
      this.save();
    },
    setTag(value) {
      this.config.tag = value;
      this.save();
    },
    setParallax(data) {
      this.config = _.merge(this.config, data);
      this.save();
    },
    setBorder(data) {
      this.config.border = `${data.size}px ${data.style} ${data.color}`;
      this.save();
    },
    setBorderRadius(data) {
      this.config.borderRadius = data.value;
      this.save();
    },
    setWidth(value) {
      this.config.width = `${value}px`;
      this.save();
    },
    setMaxWidth(value) {
      this.config.maxWidth = `${value}px`;
      this.save();
    },
    setHeight(value) {
      this.config.height = `${value}px`;
      this.save();
    },
    setMinHeight(value) {
      this.config.minHeight = `${value}px`;
      this.save();
    },
    setBackgroundColor(value) {
      this.config.backgroundColor = value;
      this.save();
    },
    setBackgroundColorHotmart(value) {
      this.config.hotmart.background = value;
      this.save();
    },
    setShadow(data) {
      this.config.boxShadow = `${data.h}px ${data.v}px ${data.b}px ${data.s}px ${data.color}  ${data.inset ? 'inset' : ''}`;
      this.save();
    },
    setBackgroundImage(data) {
      if (data.type === 'setImage') {
        this.config.backgroundColor = 'white';
        this.config.backgroundImage = data.src;
      }

      if (data.type === 'removeImage')
        this.config.backgroundImage = 'none';


      if (data.type === 'setConfig') {
        this.config.backgroundColor = 'white';
        this.config.backgroundPosition = data.position;
        this.config.backgroundSize = data.size;
        this.config.backgroundRepeat = data.repeat;
        this.config.backgroundOrigin = data.origin;
        this.config.backgroundAttachment = data.attachment;
      }

      this.save();
    },
    save() {
      this.callback(this.config);
    },
    changeUpload() {
      var file = this.$refs['inputUploadLottieFile'].files[0];
      var reader = new FileReader();
      var vm = this;
      document.querySelector("#preview-lottie-file").innerHTML = '';

      reader.onload = function(e) {
        try {
          var target = e.target;
          var data = JSON.parse(target.result);


          const lottieAnimationConfig = {
            container: document.querySelector("#preview-lottie-file"),
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData: data
          };

          Lottie.loadAnimation(lottieAnimationConfig);
          vm.file.name = file.name;
          vm.file.upload = JSON.stringify(data);
          vm.file.isValid = true;
        } catch(e) {
          vm.file.isValid = false;
        }

      };
      reader.readAsText(file);
    },
    openUploadLottieFile(openTabId = 'see-lottie-files') {
      this.$refs['lottieFileSettings'].open(openTabId);
    },
    closeUploadLottieFile() {
      this.$refs['lottieFileSettings'].close();
    },
    deleteLottieFile(file, index) {
      excludeLottieFile(file.code)
        .then(() => {
          this.reloadUserLottieFiles();
        })
        .catch(() => {

        });
    },
    uploadLottieFile() {
      if(this.file.isValid) {
        createLottieFile(this.file)
          .then(() => {
            this.reloadUserLottieFiles();
            document.querySelector("#preview-lottie-file").innerHTML = '';
            this.$refs['lottieFileSettings'].open('see-lottie-files');
            this.$refs['inputUploadLottieFile'].value = ''

          })
          .catch(() => {

          });
      }

    }
  },
};
</script>

<style lang="scss">
.user-lottie-files-content{
  max-height: 60vh;
  overflow: auto;
  .user-lottie-files__list{
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 10px;

    .user-lottie-files__list-item {
      border: 3px solid transparent;
      border-radius: 0.625rem;
      cursor: pointer;
      padding: 4px;

      &:hover {
        border: 3px solid #05c1fc;
      }

      &.lottie-files-selected {
        border: 3px solid #1d4059cc;
      }

      lottie-player {
        pointer-events: none;
      }

      .user-lottie-files__list-item-info {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 0;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/editor/apps/LottieFile/Options.vue